/**
 * CompletenessValidationSection
 *
 * Displays completeness validation results checking that content
 * includes all required specifications for the category and price tier.
 *
 * Part of Story 53: Content Completeness Validation
 *
 * Date: December 23, 2025
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ClipboardCheck,
  XCircle,
  MinusCircle,
} from 'lucide-react';
import {
  CompletenessValidationReport,
  validateCompleteness,
  getCompletenessReport,
  getCompletenessScoreColor,
  getCompletenessDecisionVariant,
  getCompletenessSeverityColor,
} from '@/services/contentReviewService';

interface CompletenessValidationSectionProps {
  generationId: string;
  autoRun?: boolean;
}

export function CompletenessValidationSection({
  generationId,
  autoRun = false,
}: CompletenessValidationSectionProps) {
  const [report, setReport] = useState<CompletenessValidationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [skipReason, setSkipReason] = useState<string | null>(null);

  // Load existing report on mount
  useEffect(() => {
    async function loadExistingReport() {
      try {
        const response = await getCompletenessReport(generationId);
        if (response.data) {
          setReport(response.data);
          setHasChecked(true);
        } else if (autoRun) {
          handleRunValidation();
        }
      } catch (err) {
        console.error('Error loading completeness report:', err);
        if (autoRun) {
          handleRunValidation();
        }
      }
    }

    loadExistingReport();
  }, [generationId, autoRun]);

  async function handleRunValidation() {
    setLoading(true);
    setError(null);
    setSkipped(false);

    try {
      const response = await validateCompleteness(generationId);
      if (response.skipped) {
        setSkipped(true);
        setSkipReason(response.reason || 'Validation skipped');
        setHasChecked(true);
      } else if (response.data) {
        setReport(response.data);
        setHasChecked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run validation');
    } finally {
      setLoading(false);
    }
  }

  // Render decision icon
  const DecisionIcon = () => {
    if (!report) return null;

    switch (report.decision) {
      case 'PASS':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'FLAG':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'BLOCK':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  // If no check has been run yet
  if (!hasChecked && !loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Spec Completeness
          </CardTitle>
          <CardDescription>
            Validate content includes all required specifications for category
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRunValidation} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Validating...
              </>
            ) : (
              'Check Spec Completeness'
            )}
          </Button>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking Spec Completeness...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Extracting specifications from content and validating against checklist...
          </p>
        </CardContent>
      </Card>
    );
  }

  // Skipped state
  if (skipped) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-muted-foreground">
            <ClipboardCheck className="h-5 w-5" />
            Completeness Check Skipped
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">{skipReason}</p>
          <Button variant="outline" onClick={handleRunValidation}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && !report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-500">
            <AlertCircle className="h-5 w-5" />
            Completeness Check Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <Button variant="outline" onClick={handleRunValidation}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Report display
  if (!report) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DecisionIcon />
            Spec Completeness
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={getCompletenessDecisionVariant(report.decision)}>
              {report.decision}
            </Badge>
            {report.severity && (
              <Badge variant={getCompletenessSeverityColor(report.severity)}>
                {report.severity}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRunValidation}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription>{report.message}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Completeness Score */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={getCompletenessScoreColor(report.completenessScore)}>
              {report.foundSpecs.length} of {report.totalRequired} specs found
              ({report.completenessScore}%)
            </span>
            <span className="text-muted-foreground">
              Weighted: {report.weightedScore}%
            </span>
          </div>
          <Progress value={report.completenessScore} className="h-3" />
        </div>

        {/* Context */}
        <div className="flex gap-2 flex-wrap">
          <Badge variant="outline">Category: {report.category}</Badge>
          <Badge variant="outline">Tier: {report.priceTier}</Badge>
          {report.missingCriticalCount > 0 && (
            <Badge variant="destructive">
              {report.missingCriticalCount} Critical Missing
            </Badge>
          )}
        </div>

        {/* Found Specs */}
        {report.foundSpecs.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Found Specifications ({report.foundSpecs.length})
            </h4>
            <div className="space-y-1">
              {report.foundSpecs.map((spec, index) => (
                <div
                  key={index}
                  className="p-2 rounded-lg border bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      <span className="text-sm font-medium">{spec.displayName}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {spec.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Value: {spec.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Missing Specs */}
        {report.missingSpecs.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              Missing Specifications ({report.missingSpecs.length})
            </h4>
            <div className="space-y-2">
              {report.missingSpecs.map((spec, index) => (
                <div
                  key={index}
                  className="p-2 rounded-lg border bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-3 w-3 text-red-500" />
                      <span className="text-sm font-medium">{spec.displayName}</span>
                    </div>
                    <Badge variant={getCompletenessSeverityColor(spec.severity)} className="text-xs">
                      {spec.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {spec.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Partial Specs */}
        {report.partialSpecs.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <MinusCircle className="h-4 w-4 text-yellow-500" />
              Partial Specifications ({report.partialSpecs.length})
            </h4>
            <div className="space-y-1">
              {report.partialSpecs.map((spec, index) => (
                <div
                  key={index}
                  className="p-2 rounded-lg border bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MinusCircle className="h-3 w-3 text-yellow-500" />
                      <span className="text-sm font-medium">{spec.displayName}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {Math.round(spec.confidence * 100)}% confidence
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Found: {spec.foundValue}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conditional Specs Info */}
        {report.conditionalSpecs.length > 0 && (
          <details className="space-y-2">
            <summary className="cursor-pointer font-medium text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-blue-500" />
              Conditional Requirements ({report.conditionalSpecs.length})
            </summary>
            <div className="mt-2 space-y-1 pl-6">
              {report.conditionalSpecs.map((spec, index) => (
                <div key={index} className="text-sm text-muted-foreground">
                  <span className="font-mono">{spec.displayName}</span>
                  {spec.triggeredBy && (
                    <span className="text-xs ml-2">(triggered by: {spec.triggeredBy})</span>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground text-right">
          Validated: {new Date(report.validatedAt).toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}
